import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import instance from "../api/axios";
import {
  listHoldBookings,
} from "../api/route";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import DoctorAssignedCard from "../components/SessionDetail/DoctorAssignedCard";
import AppointmentDetailsCard from "../components/SessionDetail/AppointmentDetailsCard";
import PatientInfoCard from "../components/SessionDetail/PatientInfoCard";
import Button from "../components/Button";
import {
  buildHoldWhatsappMessage,
  formatCountdown,
  formatDisplayDate,
  formatDisplayTime,
  getHoldBookingDraft,
  getRemainingTimeMs,
} from "../utils/holdBooking";

const timerStyle = (remainingMs) => ({
  background: remainingMs <= 3 * 60 * 1000 ? "#FEE2E2" : "#FFF7ED",
  color: remainingMs <= 3 * 60 * 1000 ? "#B91C1C" : "#C2410C",
});

const OpsHoldSummaryPage = () => {
  const { holdId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const draft = getHoldBookingDraft();
  const [loading, setLoading] = useState(!location.state?.hold);
  const [hold, setHold] = useState(location.state?.hold || null);
  const [errorMessage, setErrorMessage] = useState("");
  const [copiedState, setCopiedState] = useState("");
  const [remainingMs, setRemainingMs] = useState(
    getRemainingTimeMs(location.state?.hold?.expires_at)
  );

  useEffect(() => {
    if (hold) return;
    const fetchHold = async () => {
      setLoading(true);
      try {
        const response = await instance.get(`${listHoldBookings}?hold_id=${holdId}&limit=1`);
        const nextHold = response?.data?.holds?.[0] || null;
        if (!nextHold) {
          setErrorMessage("Hold booking not found.");
          return;
        }
        setHold(nextHold);
      } catch (error) {
        setErrorMessage("Unable to load hold booking details.");
      } finally {
        setLoading(false);
      }
    };
    fetchHold();
  }, [hold, holdId]);

  useEffect(() => {
    if (!hold?.expires_at) return undefined;
    setRemainingMs(getRemainingTimeMs(hold.expires_at));
    const intervalId = setInterval(() => {
      setRemainingMs(getRemainingTimeMs(hold.expires_at));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [hold?.expires_at]);

  const whatsappMessage = useMemo(() => {
    if (!hold?.booking_link) return "";
    return buildHoldWhatsappMessage({
      patientName: hold.patient_name,
      bookingLink: hold.booking_link,
    });
  }, [hold]);

  const handleCopy = async (value, type) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedState(type);
      setTimeout(() => setCopiedState(""), 2000);
    } catch (error) {
      setCopiedState("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-base font-semibold text-[#393938]">
        Loading held booking details...
      </div>
    );
  }

  if (!hold) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold text-[#202020]">Hold Booking Not Found</h1>
        <p className="mt-3 text-sm text-[#666]">{errorMessage || "Please create a new hold."}</p>
        <Button
          btnName="Back To Hold Form"
          className="offer-gradient text-white px-6 py-3 mt-6"
          onClick={() => navigate("/ops/hold-booking")}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen md:px-0 flex justify-center px-0 py-0 md:py-6 md:px-4">
      <div
        className="w-full max-w-[700px] md:rounded-2xl md:shadow-lg md:bg-cover md:bg-no-repeat"
        style={{
          backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
        }}
      >
        <div className="max-w-[700px] mx-auto">
          <BackHeader onBack={() => navigate("/ops/hold-booking/slots")} />
          <StepIndicator step={3} totalSteps={3} header="Share Confirmation Link" />

          <div className="mx-4 mb-4 rounded-2xl p-4 shadow-sm" style={timerStyle(remainingMs)}>
            <div className="text-sm font-semibold">Slot hold status</div>
            <div className="mt-1 text-2xl font-bold">
              {remainingMs > 0 ? formatCountdown(remainingMs) : "Expired"}
            </div>
            <div className="mt-1 text-sm">
              {remainingMs > 0
                ? "Share this link with the patient before the timer ends."
                : "This hold has expired. Reactivate it from the dashboard later if needed."}
            </div>
          </div>

          <DoctorAssignedCard doctorName={`Dr. ${hold.therapist_name}`} />
          <AppointmentDetailsCard
            date={formatDisplayDate(hold.start_time)}
            time={formatDisplayTime(hold.start_time)}
            price={`Rs ${hold.amount}`}
          />
          <PatientInfoCard
            patientName={hold.patient_name}
            patientPhone={hold.patient_phone}
            pincode={draft?.pinCode || "N/A"}
            condition={hold.pain_condition || "N/A"}
          />

          <div className="bg-white mx-3 mt-4 rounded-3xl p-5 shadow-md">
            <div className="text-lg font-semibold text-[#2E2E2E]">Hold Details</div>
            <div className="mt-3 text-sm text-[#5A5A5A]">
              Hold ID: <span className="font-semibold text-[#1D1617]">{hold.hold_id}</span>
            </div>
            <div className="mt-2 text-sm text-[#5A5A5A]">
              Pain Score: <span className="font-semibold text-[#1D1617]">{hold.pain_score || "N/A"}</span>
            </div>
            <div className="mt-2 text-sm text-[#5A5A5A]">
              Pain Duration: <span className="font-semibold text-[#1D1617]">{hold.pain_duration || "N/A"}</span>
            </div>
          </div>

          <div className="bg-white mx-3 mt-4 rounded-3xl p-5 shadow-md">
            <div className="text-lg font-semibold text-[#2E2E2E]">Patient Confirmation Link</div>
            <div className="mt-3 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-sm break-all text-[#374151]">
              {hold.booking_link}
            </div>

            <div className="mt-4 flex flex-col gap-3 md:flex-row">
              <Button
                btnName={copiedState === "link" ? "Link Copied" : "Copy Link"}
                className="offer-gradient w-full text-white px-6 py-3"
                onClick={() => handleCopy(hold.booking_link, "link")}
              />
              <Button
                btnName={copiedState === "message" ? "Message Copied" : "Copy WhatsApp Message"}
                className="w-full border border-[var(--partner-primary)] text-[var(--partner-primary)] px-6 py-3 bg-white"
                onClick={() => handleCopy(whatsappMessage, "message")}
              />
            </div>

            <div className="mt-3">
              <Button
                btnName="Open Patient Link"
                className="w-full bg-[#111827] text-white px-6 py-3"
                onClick={() => window.open(hold.booking_link, "_blank")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsHoldSummaryPage;
